import { Camera, Info, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import Cropper from 'react-easy-crop';
import { type Service } from '../../types';
import { Point, Area } from 'react-easy-crop/types';
import { Modal } from '../../components/Modal';
import classNames from 'classnames';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
const MAX_FILE_SIZE_STRING = '10MB';
const MAX_FILE_NUMBER = 5; // Maximum number of files allowed
const CROP_ASPECT_RATIO = 4 / 3; // Desired aspect ratio (adjust as needed)
const ZOOM_MIN = 1; // Minimum zoom level
const ZOOM_MAX = 3; // Maximum zoom level
const ZOOM_STEP = 0.1; // Zoom increment

export interface ImageUploaderProps {
  uploadProgress?: { [key: string]: number };
  service?: Service;
  setImagesChanged: (changed: boolean) => void;
  isUploading?: boolean;
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({
  uploadProgress,
  service,
  setImagesChanged,
  isUploading,
}) => {
  const { formatMessage } = useIntl();
  const images = useWatch({ name: 'images' });
  const { clearErrors, setError, setValue } = useFormContext();
  const { errors } = useFormState();

  const [isCropping, setIsCropping] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [initialImages, setInitialImages] = useState<string[]>([]);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState<File | null>(null);
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [croppedImages, setCroppedImages] = useState<Map<number, Blob>>(
    new Map(),
  );
  // New state for file queue
  const [fileQueue, setFileQueue] = useState<File[]>([]);

  useEffect(() => {
    if (service && service.images) {
      setInitialImages([...service.images]);
    }
  }, [service]);

  // New effect to process the next file in the queue
  useEffect(() => {
    if (fileQueue.length > 0 && !currentImage && !cropModalOpen) {
      const nextFile = fileQueue[0];
      setCurrentImage(nextFile);
      setCropModalOpen(true);
    }
  }, [fileQueue, currentImage, cropModalOpen]);

  const checkImagesChanged = (currentImages: any[]) => {
    if (!service) {
      return currentImages.length > 0;
    }
    if (currentImages.length !== initialImages.length) {
      return true;
    }
    const existingImages = currentImages.filter(
      (img) => typeof img === 'string',
    );
    if (existingImages.length !== initialImages.length) {
      return true;
    }
    const newFiles = currentImages.filter((img) => img instanceof File);
    if (newFiles.length > 0) {
      return true;
    }
    for (let i = 0; i < existingImages.length; i++) {
      if (existingImages[i] !== initialImages[i]) {
        return true;
      }
    }
    return false;
  };

  const onCropComplete = (croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const createCroppedImage = async (file: File): Promise<File> => {
    if (!croppedAreaPixels) return file;

    const image = new Image();
    image.src = URL.createObjectURL(file);
    await new Promise((resolve) => (image.onload = resolve));

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    canvas.width = croppedAreaPixels.width;
    canvas.height = croppedAreaPixels.height;

    ctx.drawImage(
      image,
      croppedAreaPixels.x,
      croppedAreaPixels.y,
      croppedAreaPixels.width,
      croppedAreaPixels.height,
      0,
      0,
      croppedAreaPixels.width,
      croppedAreaPixels.height,
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (blob) {
          const croppedFile = new File([blob], file.name, { type: file.type });
          resolve(croppedFile);
        } else {
          resolve(file); // Fallback to original if cropping fails
        }
      }, file.type);
    });
  };

  const saveCroppedImage = async () => {
    if (!currentImage || !croppedAreaPixels) return;
    setIsCropping(true);
    try {
      const croppedFile = await createCroppedImage(currentImage);
      const currentImages = images || [];
      const updatedImages = [...currentImages, croppedFile];
      setValue('images', updatedImages, {
        shouldDirty: true,
      });

      // Update cropped images map for preview
      const newCroppedImages = new Map(croppedImages);
      newCroppedImages.set(
        updatedImages.length - 1,
        await createCroppedImage(currentImage),
      );
      setCroppedImages(newCroppedImages);

      const changed = checkImagesChanged(updatedImages);
      setImagesChanged(changed);

      setFileQueue((prev) => prev.slice(1));
      setCurrentImage(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setCropModalOpen(false);
    } finally {
      setIsCropping(false);
    }
  };

  const cancelCrop = () => {
    // Skip current file and move to the next
    setFileQueue((prev) => prev.slice(1));
    setCurrentImage(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setCropModalOpen(false);
  };

  const validateAndAddImages = async (files: File[]) => {
    clearErrors('images');

    const oversizedFiles = files.filter((file) => file.size > MAX_FILE_SIZE);
    if (oversizedFiles.length > 0) {
      setError('images', {
        type: 'maxSize',
        message: formatMessage(
          {
            id: 'addService.imgMaxSize',
            defaultMessage:
              'Размер каждого файла не должен превышать {maxSize}',
          },
          { maxSize: MAX_FILE_SIZE_STRING },
        ),
      });
      return;
    }

    const currentImages = images || [];
    const availableSlots = MAX_FILE_NUMBER - currentImages.length;
    if (files.length > availableSlots) {
      setError('images', {
        type: 'maxNumber',
        message: formatMessage(
          {
            id: 'addService.maxImages',
            defaultMessage: 'Максимум {maxNumber} изображений',
          },
          { maxNumber: MAX_FILE_NUMBER },
        ),
      });
      return;
    }

    // Add files to the queue
    setFileQueue(files);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    validateAndAddImages(Array.from(files));
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files).filter((file) =>
      file.type.startsWith('image/'),
    );

    if (files.length === 0) {
      setError('images', {
        type: 'invalid',
        message: 'Пожалуйста, загружайте только изображения',
      });
      return;
    }

    validateAndAddImages(files);
  };

  const removeImage = (index: number) => {
    const currentImages = images;
    const updatedImages = currentImages.filter((_, i) => i !== index);
    setValue('images', updatedImages, { shouldValidate: true });

    // Update cropped images map
    const newCroppedImages = new Map(croppedImages);
    newCroppedImages.delete(index);
    setCroppedImages(newCroppedImages);

    const changed = checkImagesChanged(updatedImages);
    setImagesChanged(changed);
  };

  return (
    <>
      {/* Images */}
      <div>
        <div className='flex items-center justify-between mb-1'>
          <label
            htmlFor='images'
            className='block text-sm font-medium text-gray-700'
          >
            {formatMessage({
              id: 'addService.imagesLabel',
              defaultMessage: 'Фотографии (необязательно)',
            })}
          </label>
          <div className='flex items-center text-sm text-gray-500'>
            <Info className='w-4 h-4 mr-1' />
            <span>
              {formatMessage(
                {
                  id: 'addService.imgRules',
                  defaultMessage: 'Максимум 5 фото, до {maxSize} каждое',
                },
                { maxSize: MAX_FILE_SIZE_STRING },
              )}
            </span>
          </div>
        </div>

        {images && images.length > 0 && (
          <div className='grid grid-cols-5 gap-4 mb-4'>
            {images.map((image, index) => {
              const isExistingImage = typeof image === 'string';
              const uploadIndex =
                index -
                images.filter((img, i) => i < index && typeof img === 'string')
                  .length;

              return (
                <div key={index} className='relative group'>
                  <img
                    src={
                      isExistingImage
                        ? image
                        : croppedImages.get(index)
                        ? URL.createObjectURL(croppedImages.get(index)!)
                        : URL.createObjectURL(image as File)
                    }
                    alt={`Preview ${index + 1}`}
                    className='w-full h-12  md:h-24 object-cover rounded-lg'
                  />
                  {!isUploading && (
                    <button
                      type='button'
                      onClick={() => removeImage(index)}
                      className='absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity'
                    >
                      <X className='w-4 h-4' />
                    </button>
                  )}
                  {isUploading && !isExistingImage && (
                    <div className='absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center'>
                      <div className='w-4/5'>
                        <div className='h-2 bg-gray-200 rounded-full overflow-hidden'>
                          <div
                            className='h-full bg-blue-600 transition-all duration-300 ease-out'
                            style={{
                              width: `${uploadProgress[uploadIndex] || 0}%`,
                            }}
                          />
                        </div>
                        <div className='text-white text-xs text-center mt-1'>
                          {Math.round(uploadProgress[uploadIndex] || 0)}%
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-lg transition-colors ${
            isDragging
              ? 'border-blue-500 bg-blue-50'
              : errors.images
              ? 'border-red-500'
              : 'border-gray-300'
          } ${
            images && images.length >= MAX_FILE_NUMBER
              ? 'opacity-50 pointer-events-none'
              : ''
          }`}
        >
          <div className='space-y-1 text-center'>
            <Camera
              className={`mx-auto h-12 w-12 ${
                isDragging ? 'text-blue-500' : 'text-gray-400'
              }`}
            />
            <div className='flex text-sm text-gray-600'>
              <label
                htmlFor='images'
                className='relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500'
              >
                <span>
                  {formatMessage({
                    id: 'addService.downloadImg',
                    defaultMessage: 'Загрузить фото',
                  })}
                </span>
                <input
                  id='images'
                  type='file'
                  className='sr-only'
                  multiple
                  accept='image/*'
                  onChange={handleImageChange}
                  disabled={images && images.length >= MAX_FILE_NUMBER}
                />
              </label>
              <p className='pl-1'>
                {formatMessage({
                  id: 'addService.dragAndDropImg',
                  defaultMessage: 'или перетащите',
                })}
              </p>
            </div>
            <div className='text-xs text-gray-500 space-y-1'>
              <p>PNG, JPG до {MAX_FILE_SIZE_STRING}</p>
              {images && images.length > 0 && (
                <p
                  className={
                    images.length === MAX_FILE_NUMBER
                      ? 'text-yellow-600 font-medium'
                      : ''
                  }
                >
                  {formatMessage({
                    id: 'addService.selectedImages',
                    defaultMessage: 'Выбрано файлов:',
                  })}{' '}
                  {images.length}/{MAX_FILE_NUMBER}
                  {images.length === MAX_FILE_NUMBER && ' (максимум)'}
                </p>
              )}
            </div>
          </div>
        </div>
        {errors.images && (
          <p className='mt-1 text-sm text-red-600'>{errors.images.message}</p>
        )}
      </div>

      {/* Cropper Modal */}
      <Modal
        isOpen={cropModalOpen}
        onClose={() => setCropModalOpen(false)}
        title={
          <FormattedMessage
            id='crop.title'
            defaultMessage='Обрезать изображение'
          />
        }
      >
        <div className='relative h-96'>
          {currentImage && (
            <Cropper
              image={URL.createObjectURL(currentImage)}
              crop={crop}
              zoom={zoom}
              aspect={CROP_ASPECT_RATIO}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              objectFit='contain'
            />
          )}
        </div>
        {/* Zoom Slider */}
        <div className='mt-4'>
          <label
            htmlFor='zoom'
            className='block text-sm font-medium text-gray-700 mb-2'
          >
            <FormattedMessage id='crop.scale' defaultMessage='Масштаб' />
          </label>
          <input
            id='zoom'
            type='range'
            min={ZOOM_MIN}
            max={ZOOM_MAX}
            step={ZOOM_STEP}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className='w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600'
          />
          <div className='flex justify-between text-xs text-gray-500 mt-1'>
            <span>{ZOOM_MIN}x</span>
            <span>{zoom.toFixed(1)}x</span>
            <span>{ZOOM_MAX}x</span>
          </div>
        </div>
        <div className='flex justify-end space-x-2'>
          <button
            type='button'
            onClick={cancelCrop}
            className='mt-4 px-6 py-2 text-white rounded-lg focus:outline-none bg-blue-600 hover:bg-blue-700'
          >
            <FormattedMessage id='crop.skip' defaultMessage='Пропустить' />
          </button>

          <button
            onClick={saveCroppedImage}
            type='button'
            className={classNames(
              'mt-4 px-6 py-2 text-white rounded-lg focus:outline-none bg-blue-600 flex items-center justify-center',
              { 'bg-gray-400 cursor-not-allowed': isCropping },
              { 'hover:bg-blue-700': !isCropping },
            )}
            disabled={isCropping}
          >
            {isCropping ? (
              <FormattedMessage
                id='crop.saving'
                defaultMessage='Сохраняем...'
              />
            ) : (
              <FormattedMessage id='crop.save' defaultMessage='Сохранить' />
            )}
          </button>
        </div>
      </Modal>
    </>
  );
};
